// src/utils/gamificationGame.js
// Client mirror of the game-layer helpers in functions/gamificationGame.js
// (levels, tier colors). Must stay in lockstep with the engine — the profile
// docs the UI reads (gameProfiles/*) are engine-written, so this file only
// needs display math, never reward math.

export const DEFAULT_LEVELS = [
  { id: 'bronze', name: 'Bronze', xp: 0 },
  { id: 'silver', name: 'Silver', xp: 500 },
  { id: 'gold', name: 'Gold', xp: 1500 },
  { id: 'platinum', name: 'Platinum', xp: 4000 },
  { id: 'legend', name: 'Legend', xp: 10000 }
];

export const TIER_COLORS = {
  bronze: { bg: '#f3e4d5', ink: '#8a5a2b' },
  silver: { bg: '#e8eaee', ink: '#5a6472' },
  gold: { bg: '#f7ecc8', ink: '#8a6d1a' },
  platinum: { bg: '#e3e9f5', ink: '#3f5680' },
  legend: { bg: '#1b1b1f', ink: '#f5d97b' }
};

export function tierColors(levelId) {
  return TIER_COLORS[levelId] || { bg: '#eeeeee', ink: '#444444' };
}

// Premium metallic treatment (owner feedback, rehearsal pass 2026-07-06):
// gradient medal fills + ring/glow accents. gradient = medal/chip fill,
// ink = text on the gradient, solid = accent for bars/borders, glow = box
// shadow tint. Door HostGameChips carries a MIRROR of these values — keep in
// sync by hand (no shared package between the repos).
export const TIER_STYLES = {
  bronze: {
    gradient: 'linear-gradient(135deg, #f8ddbd 0%, #d99a5b 45%, #9a642f 100%)',
    ink: '#4e3113',
    solid: '#b0763c',
    glow: 'rgba(176, 118, 60, 0.35)'
  },
  silver: {
    gradient: 'linear-gradient(135deg, #ffffff 0%, #cdd4dd 45%, #97a2b1 100%)',
    ink: '#37404c',
    solid: '#8e99a8',
    glow: 'rgba(142, 153, 168, 0.35)'
  },
  gold: {
    gradient: 'linear-gradient(135deg, #fff6cc 0%, #f2ce56 45%, #c8961a 100%)',
    ink: '#5d4700',
    solid: '#d4a017',
    glow: 'rgba(212, 160, 23, 0.4)'
  },
  platinum: {
    gradient: 'linear-gradient(135deg, #f4f8ff 0%, #bfcde9 40%, #7a92c0 100%)',
    ink: '#263a5e',
    solid: '#7a92c0',
    glow: 'rgba(122, 146, 192, 0.4)'
  },
  legend: {
    gradient: 'linear-gradient(135deg, #3c3c46 0%, #191920 55%, #050507 100%)',
    ink: '#f5d97b',
    solid: '#f5d97b',
    glow: 'rgba(245, 217, 123, 0.45)'
  }
};

export function tierStyle(levelId) {
  return (
    TIER_STYLES[levelId] || {
      gradient: 'linear-gradient(135deg, #f2f2f2 0%, #d9d9d9 100%)',
      ink: '#444444',
      solid: '#9e9e9e',
      glow: 'rgba(0, 0, 0, 0.15)'
    }
  );
}

// Mirror of functions/gamificationGame.js resolveLevel.
export function resolveLevel(lifetimeXp, levels) {
  const ladder = Array.isArray(levels) && levels.length ? levels : DEFAULT_LEVELS;
  const xp = Math.max(0, Number(lifetimeXp) || 0);
  let index = 0;
  for (let i = 0; i < ladder.length; i += 1) {
    if (xp >= Number(ladder[i].xp)) index = i;
    else break;
  }
  const level = ladder[index];
  const next = index + 1 < ladder.length ? ladder[index + 1] : null;
  return {
    id: level.id,
    name: level.name,
    index,
    xp: Number(level.xp) || 0,
    nextId: next ? next.id : null,
    nextName: next ? next.name : null,
    nextXp: next ? Number(next.xp) : null,
    progress: next ? Math.min(1, (xp - level.xp) / (next.xp - level.xp)) : 1
  };
}

export const BADGE_METRIC_LABELS = {
  seasonPayers: 'Paying guests in one season',
  nightPayers: 'Paying guests in one night',
  seasonBirthdayShows: 'Birthday shows in one season',
  seasonBoothSales: 'Booth sales ($) in one season',
  seasonSignIns: 'Sign-ins in one season',
  recruitsActivated: 'Recruits activated in one season',
  streakWeeks: 'Week streak',
  anniversaryYears: 'Years with the brand'
};
